import { apiErrorField } from '@agos/api-client'
import { parseMoneyToCents, tt } from '@agos/shared'

/**
 * A customer's own order history.
 *
 * **Cancelling is narrower than it looks.** `CustomerOrderService.cancel` compares against a single
 * status — `CANCELLABLE_STATUS = "new"` — and answers 409 for anything else: *"Objednávku už nie je
 * možné zrušiť — je v príprave alebo doručená."* Measured across the 10 491 orders on this data:
 * **10 149 delivered, 334 cancelled, 4 new, 3 quality_check, 1 preparing.** So the button applies to
 * four rows in the entire history, and showing it on the other 10 487 would be offering something that
 * answers an error.
 *
 * `hasMore` comes from the server asking for one row more than it returns, so there is no count query
 * over a table this size and no page number to be wrong about.
 */

export interface CustomerOrder {
  id: number
  orderNumber: string
  status: string
  type: string
  totalCents: number
  paymentMethod: string
  customerAddress: string
  createdAt: string
  scheduledAt: string | null
  isPreorder: boolean
}

export interface OrderPage {
  orders: CustomerOrder[]
  hasMore: boolean
  offset: number
  limit: number
}

/** The one status the server will cancel. */
const CANCELLABLE = 'new'

export function ordersOf(payload: unknown): OrderPage {
  const row = (typeof payload === 'object' && payload !== null ? payload : {}) as Record<string, unknown>
  const rows = Array.isArray(row.orders) ? row.orders : []
  return {
    orders: rows.map((entry) => {
      const order = entry as Record<string, unknown>
      return {
        id: typeof order.id === 'number' ? order.id : 0,
        orderNumber: typeof order.orderNumber === 'string' ? order.orderNumber : '',
        status: typeof order.status === 'string' ? order.status : '',
        type: typeof order.type === 'string' ? order.type : '',
        totalCents: parseMoneyToCents(order.total as string | number | null),
        paymentMethod: typeof order.paymentMethod === 'string' ? order.paymentMethod : '',
        customerAddress: typeof order.customerAddress === 'string' ? order.customerAddress : '',
        createdAt: typeof order.createdAt === 'string' ? order.createdAt : '',
        scheduledAt: typeof order.scheduledAt === 'string' ? order.scheduledAt : null,
        isPreorder: order.isPreorder === true,
      }
    }),
    hasMore: row.hasMore === true,
    offset: typeof row.offset === 'number' ? row.offset : 0,
    limit: typeof row.limit === 'number' ? row.limit : 20,
  }
}

/**
 * Whether this order can still be called off.
 *
 * One status, matching the server exactly. Anything wider offers a button that answers 409, and the
 * customer reads that as the site being broken rather than as the kitchen having started.
 */
export function mayCancel(order: CustomerOrder): boolean {
  return order.status === CANCELLABLE
}

/**
 * What a cancel is about to do, said before it is done - §9.396, FB-705.
 *
 * <p><b>Legacy asks first and AGOS did not.</b> `MojProfil.tsx` puts this behind `askConfirm` with
 * *"Túto akciu nie je možné vrátiť späť"*; the port cancelled on a single click. It is a destructive
 * action on somebody's own order, and the one-click version is the kind of mistake a customer makes
 * with a thumb on a phone.
 */
export function cancelConfirmation(): { title: string; detail: string; confirm: string } {
  return {
    title: tt('Zrušiť objednávku?'),
    detail: tt('Objednávka bude označená ako zrušená. Túto akciu nie je možné vrátiť späť.'),
    confirm: tt('Zrušiť objednávku'),
  }
}

/**
 * What to say once the server has answered - §9.396.
 *
 * <p><b>The refusal is the point.</b> The server allows a cancel only while the order is `new` and
 * otherwise answers 409 with a sentence that names itself; the page offered the button from its own
 * copy of the status, so the two disagree the moment the kitchen accepts an order. Reading the
 * server's words is what turns *nothing happened* into *it is already in preparation*.
 */
export function cancelSucceeded(): string {
  return tt('Objednávka bola zrušená.')
}

/**
 * The server's own sentence for a refused cancel, then the fallback - §9.396.
 *
 * <p>Two names rather than one function with a `null` sentinel: `unknown | null` collapses to
 * `unknown` and lint says so, and a caller reading `cancelOutcome(null)` at a success site has to
 * know the convention. The pair says which case it is at the call site.
 */
export function cancelFailed(failure: unknown): string {
  return apiErrorField(failure, 'error') ?? tt('Nepodarilo sa zrušiť objednávku.')
}

/** Said in place of the cancel button, when there is nothing to offer. */
export function cancelNote(order: CustomerOrder): string {
  if (mayCancel(order)) return ''
  if (order.status === 'cancelled') return tt('Zrušená.')
  if (order.status === 'delivered') return tt('Doručená.')
  return tt('Objednávku už nie je možné zrušiť - je v príprave.')
}

/**
 * The statuses this build can name, and what it says for one it cannot.
 *
 * FB-166's lesson again: a status key with no label is *reported*, not rendered blank. The set is taken
 * from the data — `delivered`, `cancelled`, `new`, `quality_check`, `preparing` — plus the ones the
 * kitchen board uses, so a status that appears later is visible as unknown rather than as nothing.
 */
const KNOWN_STATUSES = [
  'new',
  'confirmed',
  'preparing',
  'quality_check',
  'ready',
  'out_for_delivery',
  'delivered',
  'cancelled',
] as const

/**
 * Each `tt()` call is written out rather than looked up from a map of bare strings, so the extractor
 * sees every one of them and the lookup still happens at render rather than at module load — which
 * would pin the language at import time and defeat the switcher.
 */
export function statusLabel(status: string): string {
  switch (status) {
    case 'new':
      return tt('Prijatá')
    case 'confirmed':
      return tt('Potvrdená')
    case 'preparing':
      return tt('Pripravuje sa')
    case 'quality_check':
      return tt('Kontrola kvality')
    case 'ready':
      return tt('Pripravená')
    case 'out_for_delivery':
      return tt('Na ceste')
    case 'delivered':
      return tt('Doručená')
    case 'cancelled':
      return tt('Zrušená')
    default:
      return `${tt('Neznámy stav')}: ${status}`
  }
}

export function isUnknownStatus(status: string): boolean {
  return !(KNOWN_STATUSES as readonly string[]).includes(status)
}

/** Legacy's page size, for both the order history and the referral list. */
export const ORDERS_PAGE_SIZE = 20

/**
 * How large the window should be after *load more*, or `null` when there is nothing more.
 *
 * **A growing window rather than accumulated pages**, which is what `nextOffset` used to describe and
 * nobody ever called. Legacy fetches `offset=orders.length` and concatenates; that means holding the
 * merge by hand, and a merge is where duplicates and gaps live when an order is placed mid-scroll.
 * Asking for `limit=40, offset=0` gets the same forty rows in one answer the query client can cache,
 * and the most any customer on this data has is **82** — one request, not a paging protocol.
 */
export function nextLimit(page: OrderPage, pageSize = ORDERS_PAGE_SIZE): number | null {
  return page.hasMore ? page.orders.length + pageSize : null
}

/**
 * One line of a past order — §9.247, FB-407, the owner's N2.
 *
 * *"história objednávka alias Moje objednávky, is missing what was actually ordered."* It was: the
 * row printed a number, a status and a total, and the detail endpoint that `again()` already calls to
 * rebuild a basket was never asked to show one.
 *
 * `GET /customer-auth/my-orders/{id}` answers `{order, items}`, and the items carry
 * `productName`, `quantity`, `totalPrice`, the box group and **`giftRuleId`** — which is what lets a
 * gift be marked as one, exactly as the basket marks it.
 */
export interface OrderLineRow {
  id: number
  name: string
  quantity: number
  totalCents: number
  /** *Burger box · Hlavné jedlo*, when the line came out of one. */
  boxGroupName: string | null
  boxSlotLabel: string | null
  isGift: boolean
}

export function orderItemsOf(payload: unknown): OrderLineRow[] {
  const rows = (payload as { items?: unknown } | undefined)?.items
  if (!Array.isArray(rows)) return []
  return rows.map((entry, index) => {
    const row = entry as Record<string, unknown>
    return {
      id: typeof row.id === 'number' ? row.id : index,
      // A line with no name is still a line the customer paid for; naming it nothing is better than
      // dropping it out of a list they are reading their order back from.
      name: typeof row.productName === 'string' && row.productName !== '' ? row.productName : '-',
      quantity: typeof row.quantity === 'number' ? row.quantity : 1,
      totalCents: parseMoneyToCents(row.totalPrice as string | number | null),
      boxGroupName: typeof row.boxGroupName === 'string' && row.boxGroupName !== '' ? row.boxGroupName : null,
      boxSlotLabel: typeof row.boxSlotLabel === 'string' && row.boxSlotLabel !== '' ? row.boxSlotLabel : null,
      isGift: row.giftRuleId !== null && row.giftRuleId !== undefined,
    }
  })
}

/** The three sums legacy prints under the items. `null` where the order has none. */
export interface OrderTotals {
  subtotalCents: number
  deliveryCents: number | null
  discountCents: number | null
}

export function orderTotalsOf(payload: unknown): OrderTotals {
  const order = (payload as { order?: unknown } | undefined)?.order
  const row = (typeof order === 'object' && order !== null ? order : {}) as Record<string, unknown>
  const delivery = parseMoneyToCents(row.deliveryCost as string | number | null)
  const discount = parseMoneyToCents(row.discount as string | number | null)
  return {
    subtotalCents: parseMoneyToCents(row.subtotal as string | number | null),
    // Legacy prints these two only when they are non-zero — a *Rozvoz 0,00 €* line on a pickup order
    // is noise, and a *Zľava 0,00 €* reads as a discount that failed.
    deliveryCents: delivery > 0 ? delivery : null,
    discountCents: discount > 0 ? discount : null,
  }
}

/** *Rozvoz* or *Vyzdvihnutie* — legacy's two words for the same field. */
export function orderTypeLabel(type: string): string {
  return type === 'delivery' ? tt('Rozvoz') : tt('Vyzdvihnutie')
}

/**
 * The colour a status pill carries — §9.247.
 *
 * Legacy tints each status; AGOS printed the label in the body colour, so *zrušená* and *doručená*
 * looked the same at a glance. Grouped rather than mapped one-to-one: what a customer needs from the
 * colour is finished / in progress / stopped, and a per-status palette is a table to keep in step
 * with a status list that `isUnknownStatus` already admits can grow.
 */
export function statusTone(status: string): 'done' | 'stopped' | 'active' {
  if (status === 'delivered' || status === 'completed' || status === 'picked_up') return 'done'
  if (status === 'cancelled' || status === 'rejected') return 'stopped'
  return 'active'
}
